import { ErrorMessage, useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { expenseActions } from "../../sagas/expenseSlice";
import * as Yup from 'yup';
import { Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap";
import { FaCalendar, FaCreditCard, FaCubes, FaDollarSign, FaEdit, FaFilter, FaPlus, FaSearch, FaTrash, FaUser } from "react-icons/fa";
import Swal from "sweetalert2";
import { formatPrice } from "../../helpers/utility";
import Select from "react-select";
import { debounce } from "lodash";
import { commercialActions } from "../../sagas/commercialSlice";

const ExpenseList = () => {
  const dispatch = useDispatch();
  const { list, categories, loading } = useSelector((state) => state.expenses); // Adjust state path as needed
  const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials); // Adjust state path as needed
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { t } = useTranslation();
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [filters, setFilters] = useState({
    commercial: null,
    category: null,
    dateFrom: formatDate(todayDate),
    dateTo: formatDate(tomorrowDate),
    query: ''
  });

  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(expenseActions.list({ ...filters })); // Fetch categories on component mount
  }, []);

  useEffect(() => {
    dispatch(expenseActions.list({ ...filters })); // Fetch categories on component mount
  }, [filters]);


  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [list]);

  const clearFilters = () => {
    setFilters({
      query: '',
      dateFrom: formatDate(todayDate),
      dateTo: formatDate(tomorrowDate)
    });
  };

  const commercialOptions = commercialList?.map(com => ({
    value: com.id,
    label: com.fullName,
  })) ?? [];

  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(expenseActions.list({
        ...filters,
        query: query
      }));
    }, 1500), // Wait 1.5 seconds after user stops typing
    [filters.query]
  );


  const handleCloseModal = () => {
    setIsModalVisible(false);
    formik.resetForm();
    dispatch(expenseActions.list({ ...filters })); // Fetch categories on component mount
  };

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };


  const formik = useFormik({
    initialValues: {
      id: null,
      numero: '',
      dateOperation: new Date().toISOString().split('T')[0],
      commentaire: '',
      montantPaye: 0,
      modeReglement: '',
      numeroCheque: '',
      banque: '',
      dateEcheance: null,
    },

    onSubmit: (values) => {
      let payload = {
        data: {
          id: values.id,
          numero: values.numero,
          dateOperation: values.dateOperation,
          commentaire: values.commentaire,
          montantPaye: values.montantPaye,
          modeReglement: values.modeReglement,
          numeroCheque: values.numeroCheque,
          banque: values.banque,
          dateEcheance: values.dateEcheance,
        },
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: 'Succès',
            text: 'Catégorie enregistrés avec succès!',
            confirmButtonText: 'OK',
          });
          handleCloseModal();
        }
      }
      if (values.id) {
        dispatch(expenseActions.update(payload));
      } else {
        dispatch(expenseActions.create(payload));
      }
    },
  });

  const categorieOptions = categories?.map(categorie => ({
    value: categorie.id,
    label: categorie.libelle,
  })) ?? [];

  const handleCategoryChange = (value) => {
    formik.setFieldValue('categorieId', value);
  };

  const handleCommercialChange = (value) => {
    formik.setFieldValue('commercialId', value);
  };

  const handleAddExpense = () => {
    if (formik.values.montantPaye <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Avertissement',
        text: 'Veuillez entrer un montant valide.',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (formik.values.modeReglement === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Avertissement',
        text: 'Veuillez sélectionner un mode de règlement.',
        confirmButtonText: 'OK',
      });
      return;
    }

    formik.handleSubmit();
  }

  const handleDeleteExpense = (id) => {
    dispatch(expenseActions.delete(id));
  };

  const handleEditExpense = (id) => {
    const expense = list.find(exp => exp.id === id);
    if (expense) {
      formik.setValues(expense);
      toggleModal();
    }
  };

  return (
    <Container fluid>
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          <Row className="mb-4" >
            <Col>
              <Card className="shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCubes className="text-primary me-2" />
                    {t('expense.list')}
                  </h4>
                  <Button
                    color="primary"
                    onClick={() => toggleModal()} // Go back to the previous page
                    className="d-flex align-items-center"
                  >
                    <FaPlus className="me-2" />
                    {t('expense.new')}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Card className="shadow-sm">
            <CardBody>
              <div className="filter-container mb-4">
                <Row className="mb-3 g-2 align-items-end">
                  <Col lg={5} md={5} sm={6} xs={12}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">{t('reglement.search')}</Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FaSearch />
                        </span>
                        <Input
                          type="text"
                          innerRef={searchInputRef}
                          placeholder={t('expense.searchPlaceholder')}
                          value={filters.query}
                          onChange={e => {
                            const value = e.target.value.toUpperCase();
                            setFilters(prevFilters => ({
                              ...prevFilters,
                              query: value
                            }));
                            debouncedSearch(value);
                          }}
                          className="text-uppercase"
                          style={{ textTransform: 'uppercase' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              debouncedSearch(e.target.value.toUpperCase());
                            }
                          }}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  
                  <Col lg={3} md={3} sm={6} xs={12}>
                    <FormGroup className="mb-0">
                      <Label for="dateFrom" className="mb-1">Date début</Label>
                      <Input
                        type="date"
                        id="dateFrom"
                        value={filters.dateFrom}
                        onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={3} md={3} sm={6} xs={12}>
                    <FormGroup className="mb-0">
                      <Label for="dateTo" className="mb-1">Date fin</Label>
                      <Input
                        type="date"
                        id="dateTo"
                        value={filters.dateTo}
                        onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                    <Button
                      color="secondary"
                      onClick={clearFilters}
                      title={t('common.clearFilters')}
                      className="mb-3"
                      style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <FaFilter />
                    </Button>
                  </Col>
                </Row>
              </div>
              <Table bordered>
                <thead>
                  <tr>
                    <th>{t('expense.numero')}</th>
                    <th>{t('expense.dateOperation')}</th>
                    <th>{t('expense.comment')}</th>
                    <th>{t('expense.montantPaye')}</th>
                    <th>{t('expense.modeReglement')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length > 0 && list.map((ex) => (
                    <tr key={ex.id}>
                      <td>{ex.numero}</td>
                      <td>{ex.dateOperation}</td>
                      <td>{ex.commentaire}</td>
                      <td>{ex.montantPaye}</td>
                      <td>
                        <div>
                          <div>{ex.modeReglement}</div>
                          {ex.modeReglement === 'CHEQUE' && (
                            <div className="text-muted small">
                              {ex.numeroCheque && <div>{ex.numeroCheque}</div>}
                              {ex.dateEcheance && <div>{ex.dateEcheance}</div>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteExpense(ex.id)}
                        >
                          <FaTrash />
                        </Button>
                        <Button
                          className="ms-1"
                          color="warning"
                          size="sm"
                          onClick={() => handleEditExpense(ex.id)}
                        >
                          <FaEdit />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-end fw-bold">{t('expense.total')}</td>
                    <td>{formatPrice(list.reduce((acc, cat) => acc + cat.montantPaye, 0))}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </Table>
              {list.length === 0 && (
                <div className="text-center mt-4">
                  <FaCubes size={50} className="text-muted" />
                  <h5 className="mt-2">{t('expense.noExpenses')}</h5>
                </div>
              )}
            </CardBody>
          </Card>
        </>)}
      <Modal isOpen={isModalVisible} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          {formik.values.id ? `${t('expense.edit')} ${formik.values.numero}` : t('expense.new')}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              
             
              <Col md={4}>
                <FormGroup>
                  <Label for="montantPaye" className="form-label">
                    <FaDollarSign className="me-1" /> {t('expense.montantPaye')}
                  </Label>
                  <Input
                    id="montantPaye"
                    name="montantPaye"
                    type="number"
                    onFocus={(e) => e.target.select()}
                    value={formik.values.montantPaye}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="dateOperation" className="form-label">
                    <FaCalendar className="me-1" /> {t('expense.dateOperation')}
                  </Label>
                  <Input
                    id="dateOperation"
                    name="dateOperation"
                    type="date"
                    value={formik.values.dateOperation}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="modeReglement" className="form-label">
                    <FaCreditCard className="me-1" /> {t('expense.paymentMethod.title')}
                  </Label>
                  <Input
                    type="select"
                    id="modeReglement"
                    name="modeReglement"
                    placeholder={t('expense.paymentMethod.placeholder')}
                    value={formik.values.modeReglement}
                    onChange={formik.handleChange}
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            {formik.values.modeReglement === 'CHEQUE' && (
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label for="numeroCheque">{t('expense.numeroCheque')}</Label>
                    <Input
                      id="numeroCheque"
                      name="numeroCheque"
                      value={formik.values.numeroCheque || ''}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="banque">{t('expense.banque')}</Label>
                    <Input
                      type="select"
                      id="banque"
                      name="banque"
                      value={formik.values.banque}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.banque && formik.errors.banque}
                    >
                      <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                      <option value="BMCE">BMCE</option>
                      <option value="ATTIJARI">ATTIJARI</option>
                      <option value="CIH">CIH</option>
                      <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                      <option value="SG">SG</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="dateEcheance">{t('expense.dateEcheance')}</Label>
                    <Input
                      id="dateEcheance"
                      name="dateEcheance"
                      type="date"
                      value={formik.values.dateEcheance || ''}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>{t('expense.comment')}</Label>
                  <Input
                    type="textarea"
                    name="commentaire"
                    value={formik.values.commentaire || ''}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddExpense}>
            {t('common.save')}
          </Button>{' '}
          <Button color="secondary" onClick={toggleModal}>
            {t('common.cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default ExpenseList;
